import { Component, OnInit, OnDestroy } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import * as _ from 'lodash';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import { filter, first, map, tap, switchMap, takeUntil } from 'rxjs/operators';
import { Custodian, Item, ItemExtended, Site } from 'src/app/models';
import * as cartActions from '../../actions/cart.actions';
import * as fromRoot from '../../reducers';
import { AddCartToShippingOrderDialogComponent } from './add-cart-to-shipping-order-dialog/add-cart-to-shipping-order-dialog.component';
import { ItemsService } from 'src/app/services/items.service';
import { ShippingOrdersService } from 'src/app/services/shipping-orders.service';
import { ComponentType, ItemStatus } from 'src/app/enums';
import { SubCustodyAssignmentsService } from 'src/app/services/sub-custody-assignments.service';
import { BuildingsService } from 'src/app/services/buildings.service';
import { RoomsService } from 'src/app/services/rooms.service';
import { SitesService } from 'src/app/services/sites.service';
import { UpdateItemLocationDialogComponent } from '../items/update-item-location-dialog.component';
import { UpdateItemLocations } from 'src/app/interfaces';
import { CustodiansService } from 'src/app/services/custodians.service';
import { ProjectsService } from 'src/app/services/projects.service';
import { UpdateProjectForCartItemsDialogComponent } from './update-project-for-cart-items-dialog/update-project-for-cart-items-dialog.component';
import { UpdateClassificationDialogComponent } from './update-classification-dialog/update-classification-dialog.component';
import { UpdateComponentTypeDialogComponent } from './update-component-type-dialog/update-component-type-dialog.component';
import { TransferCartItemsDialogComponent } from './transfer-cart-items-dialog/transfer-cart-items-dialog.component';
import { BuildingCreate } from 'src/app/models/building';
import { RoomCreate } from 'src/app/models/room';
import { UpdateLastInventoriedDateDialogComponent } from './update-last-inventoried-date-dialog/update-last-inventoried-date-dialog.component';
import { CheckInCartItemsDialogComponent } from './check-in-cart-items-dialog/check-in-cart-items-dialog.component';

@Component({
  selector: 'app-cart',
  templateUrl: './cart.component.html',
  styleUrls: ['./cart.component.scss'],
})
export class CartComponent implements OnInit, OnDestroy {
  public itemsInCart$: Observable<ItemExtended[]>;
  public loaded$: Observable<boolean>;
  public loading$ = new BehaviorSubject<boolean>(false);
  public cartActions$: Observable<any[]>;
  private destroy$ = new Subject<void>();

  constructor(
    private itemsService: ItemsService,
    private router: Router,
    private custodiansService: CustodiansService,
    private shippingOrdersService: ShippingOrdersService,
    private subCustodyAssignmentsService: SubCustodyAssignmentsService,
    private store: Store<fromRoot.State>,
    private buildingsService: BuildingsService,
    private roomsService: RoomsService,
    private sitesService: SitesService,
    private projectsService: ProjectsService,
    public dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.loaded$ = this.itemsService.loaded$;
    const itemMap$ = this.itemsService.entityMap$.pipe(filter((itemMap) => !_.isEmpty(itemMap)));
    
    this.itemsInCart$ = combineLatest([this.store.select(fromRoot.selectCartIds), itemMap$]).pipe(
      map(([cartIds, itemMap]) => {
        // Only display valid items
        return cartIds.filter((id) => itemMap[id]).map((id) => itemMap[id]);
      }),
    );

    // Initialize cart actions
    this.cartActions$ = this.itemsInCart$.pipe(
      map((items) => {
        const shippedItemsCount = items.filter(item => item.status === ItemStatus.Shipped).length;
        
        return [
          {
            text: 'Add to Existing Shipping Order',
            iconClass: 'far fa-layer-plus',
            click: () => this.openAddItemsToShippingOrderDialog(),
          },
          { text: 'New Shipping Order', iconClass: 'far fa-plus', click: () => this.openNewShippingOrderModal() },
          {
            text: 'New Sub-Custody Assignment',
            iconClass: 'far fa-plus',
            click: () => this.openNewSubCustodyAssignmentModal(),
          },
          {
            text: 'Update Location for Items',
            iconClass: 'far fa-map-marker-alt',
            click: () => this.openUpdateLocationDialog(),
          },
          { text: 'Update Project', iconClass: 'far fa-layer-plus', click: () => this.openUpdateProjectDialog() },
          { text: 'Update Classification', iconClass: 'far fa-key', click: () => this.openUpdateClassificationDialog() },
          { text: 'Update Component Type', iconClass: 'far fa-tag', click: () => this.openUpdateComponentTypeDialog() },
          { text: 'Update Last Inventoried Date', iconClass: 'far fa-calendar-alt', click: () => this.openUpdateLastInventoriedDateDialog() },
          { text: 'Transfer to Site', iconClass: 'far fa-exchange-alt', click: () => this.openTransferCartItemsDialog() },
          { 
            text: `Check In Items (${shippedItemsCount})`, 
            iconClass: 'far fa-box-check', 
            click: () => this.openCheckInCartItemsDialog(),
            disabled: shippedItemsCount === 0
          },
          { text: 'Clear Cart', iconClass: 'far fa-layer-plus', click: () => this.clearCart() },
        ];
      })
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public clearCart() {
    this.store.dispatch(cartActions.reset());
  }

  public openAddItemsToShippingOrderDialog() {
    this.shippingOrdersService.entities$
      .pipe(
        first(),
        tap((shippingOrders) => {
          const dialogRef = this.dialog.open(AddCartToShippingOrderDialogComponent, {
            disableClose: true,
            minWidth: '40%',
            data: {
              shippingOrders,
            },
          });
          dialogRef.afterClosed().subscribe((shippingOrderId: number) => {
            if (shippingOrderId) {
              this.addItemsToShippingOrder(shippingOrderId);
            }
          });
        }),
      )
      .subscribe();
  }

  public openItemEditDialog(itemId: number) {
    this.itemsService.openEditDialog(itemId).subscribe();
  }

  public openNewShippingOrderModal() {
    this.shippingOrdersService.openNewDialog().subscribe((newShippingOrder) => {
      if (newShippingOrder) {
        this.addItemsToShippingOrder(newShippingOrder.id);
      }
    });
  }

  public openNewSubCustodyAssignmentModal() {
    this.subCustodyAssignmentsService.openNewDialog().subscribe((newSubCustodyAssignment) => {
      if (newSubCustodyAssignment) {
        this.addItemsToSubCustodyAssignment(newSubCustodyAssignment.id, newSubCustodyAssignment.custodianId);
      }
    });
  }

  public openUpdateProjectDialog() {
    this.projectsService.entities$
      .pipe(
        first(),
        tap((projects) => {
          const dialogRef = this.dialog.open(UpdateProjectForCartItemsDialogComponent, {
            minWidth: '40%',
            disableClose: true,
            data: {
              projects,
            },
          });
          dialogRef.afterClosed().subscribe((projectId: number) => {
            if (projectId) {
              this.updateProjectForItems(projectId);
            }
          });
        }),
      )
      .subscribe();
  }

  public openUpdateClassificationDialog() {
    const dialogRef = this.dialog.open(UpdateClassificationDialogComponent, {
      minWidth: '40%',
      disableClose: true,
      data: {
        classificationOptions: this.itemsService.classificationOptions,
      },
    });
    return dialogRef.afterClosed().subscribe((classification: string) => {
      if (classification) {
        this.updateClassificationForItems(classification);
      }
    });
  }

  public openUpdateComponentTypeDialog() {
    const dialogRef = this.dialog.open(UpdateComponentTypeDialogComponent, {
      minWidth: '40%',
      disableClose: true,
      data: {
        componentTypes: this.itemsService.componentTypes,
      },
    });
    return dialogRef.afterClosed().subscribe((componentType: ComponentType) => {
      if (componentType) {
        this.updateComponentTypeForItems(componentType);
      }
    });
  }

  public openUpdateLocationDialog() {
    this.loading$.next(true);
    const modalDataReady$ = combineLatest([
      this.buildingsService.loaded$,
      this.roomsService.loaded$,
      this.sitesService.loaded$,
      this.custodiansService.loaded$,
    ]).pipe(
      map(
        ([buildingsLoaded, roomsLoaded, sitesLoaded, custodiansLoaded]) =>
          buildingsLoaded && roomsLoaded && sitesLoaded && custodiansLoaded,
      ),
    );

    combineLatest([modalDataReady$, this.sitesService.userSites$, this.custodiansService.entities$, this.itemsInCart$])
      .pipe(
        filter(([modalDataReady, sites, custodians]: [boolean, Site[], Custodian[], ItemExtended[]]) => modalDataReady),
        first(),
        switchMap(([modalDataReady, sites, custodians, items]) => {
          const dialogRef = this.dialog.open(UpdateItemLocationDialogComponent, {
            minWidth: '60%',
            disableClose: true,
            data: {
              items,
              sites,
              custodians,
            },
          });

          return dialogRef.afterClosed();
        }),
        switchMap((result: UpdateItemLocations) => {
          if (result) {
            return this.itemsService.updateLocations(result);
          } else {
            return of(false);
          }
        }),
      )
      .subscribe((savedChanges: boolean) => {
        if (savedChanges) {
          this.clearCart();
          this.router.navigate(['/']);
        }
        this.loading$.next(false);
      });
  }

  public removeFromCart(id: number) {
    this.store.dispatch(cartActions.removeItem({ id }));
  }

  private addItemsToShippingOrder(shippingOrderId: number) {
    this.itemsInCart$
      .pipe(
        first(),
        switchMap((items) => {
          const updates = items.map((item) => {
            return this.itemsService
              .update({
                ...item,
                assignedToId: null,
                shippingOrderId: shippingOrderId,
                status: ItemStatus.Shipped,
                subCustodyAssignmentId: null,
              })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
          });

          return combineLatest(updates);
        }),
        tap(() => this.router.navigate(['./shipping-order', shippingOrderId])),
      )
      .subscribe();
  }

  private updateProjectForItems(projectId: number) {
    this.itemsInCart$
      .pipe(
        first(),
        switchMap((items) => {
          const updates = items.map((item) => {
            return this.itemsService
              .update({ ...item, projectId })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
          });

          return combineLatest(updates);
        }),
      )
      .subscribe();
  }

  private updateClassificationForItems(classification: string) {
    this.itemsInCart$
      .pipe(
        first(),
        switchMap((items) => {
          const updates = items.map((item) => {
            return this.itemsService
              .update({ ...item, classification })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
          });

          return combineLatest(updates);
        }),
      )
      .subscribe();
  }

  private updateComponentTypeForItems(componentType: ComponentType) {
    this.itemsInCart$
      .pipe(
        first(),
        switchMap((items) => {
          const updates = items.map((item) => {
            return this.itemsService
              .update({ ...item, componentType: componentType })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
          });

          return combineLatest(updates);
        }),
      )
      .subscribe();
  }

  private addItemsToSubCustodyAssignment(subCustodyAssignmentId: number, assignedToId: number) {
    this.itemsInCart$
      .pipe(
        first(),
        switchMap((items) => {
          const updates = items.map((item) => {
            return this.itemsService
              .update({
                ...item,
                assignedToId: assignedToId,
                status: ItemStatus.CheckedOut,
                subCustodyAssignmentId,
              })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
          });

          return combineLatest(updates);
        }),
        tap(() => this.router.navigate(['./sub-custody-assignment', subCustodyAssignmentId])),
      )
      .subscribe();
  }

  public openTransferCartItemsDialog() {
    this.loading$.next(true);
    combineLatest([this.sitesService.userSites$, this.itemsInCart$])
      .pipe(
        first(),
        switchMap(([sites, items]) => {
          const dialogRef = this.dialog.open(TransferCartItemsDialogComponent, {
            minWidth: '40%',
            disableClose: true,
            data: {
              items,
              sites,
            },
          });

          return dialogRef.afterClosed();
        }),
        switchMap((targetSiteId: number) => {
          if (targetSiteId) {
            return this.transferItemsToSite(targetSiteId);
          } else {
            return of(false);
          }
        }),
      )
      .subscribe((success: boolean) => {
        if (success) {
          this.clearCart();
          this.router.navigate(['/']);
        }
        this.loading$.next(false);
      });
  }

  private transferItemsToSite(targetSiteId: number) {
    return this.itemsInCart$.pipe(
      first(),
      switchMap((items) => {
        // Group items by building and room
        const buildingRoomMap = new Map<string, { buildingName: string; roomName: string; items: ItemExtended[] }>();
        
        items.forEach((item) => {
          const key = `${item.buildingName}-${item.roomName}`;
          if (!buildingRoomMap.has(key)) {
            buildingRoomMap.set(key, {
              buildingName: item.buildingName,
              roomName: item.roomName,
              items: [],
            });
          }
          buildingRoomMap.get(key).items.push(item);
        });

        // Process each building/room combination
        const buildingRoomOperations = Array.from(buildingRoomMap.values()).map(async ({ buildingName, roomName, items }) => {
          // Check if building exists in target site
          const buildings = await this.buildingsService.entities$.pipe(first()).toPromise();
          let building = buildings.find((b) => b.name === buildingName && b.siteId === targetSiteId);

          if (!building) {
            // Create new building
            const buildingCreate: BuildingCreate = {
              name: buildingName,
              siteId: targetSiteId,
            };
            building = await this.buildingsService
              .addBuilding(buildingCreate)
              .pipe(first())
              .toPromise();
          }

          // Check if room exists in building
          const rooms = await this.roomsService.entities$.pipe(first()).toPromise();
          let room = rooms.find((r) => r.name === roomName && r.buildingId === building.id);

          if (!room) {
            // Create new room
            const roomCreate: RoomCreate = {
              name: roomName,
              buildingId: building.id,
            };
            room = await this.roomsService
              .addRoom(roomCreate)
              .pipe(first())
              .toPromise();
          }

          // Update items to reference new building and room
          const itemUpdates = items.map((item) =>
            this.itemsService
              .update({
                ...item,
                buildingId: building.id,
                roomId: room.id,
                siteId: targetSiteId,
              })
              .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id))),
          );

          return combineLatest(itemUpdates).toPromise();
        });

        return combineLatest(buildingRoomOperations);
      }),
      map(() => true),
    );
  }

  public openUpdateLastInventoriedDateDialog() {
    this.itemsInCart$.pipe(
      first(),
      switchMap((items) => {
        const dialogRef = this.dialog.open(UpdateLastInventoriedDateDialogComponent, {
          minWidth: '40%',
          disableClose: true,
          data: {
            itemCount: items.length,
          },
        });

        return dialogRef.afterClosed();
      }),
      switchMap((lastInventoriedDate: string) => {
        if (lastInventoriedDate) {
          return this.updateLastInventoriedDateForItems(lastInventoriedDate);
        } else {
          return of(false);
        }
      }),
    ).subscribe();
  }

  private updateLastInventoriedDateForItems(lastInventoriedDate: string) {
    return this.itemsInCart$.pipe(
      first(),
      switchMap((items) => {
        const updates = items.map((item) => {
          return this.itemsService
            .update({ ...item, lastInventoriedDate })
            .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
        });

        return combineLatest(updates);
      }),
    );
  }

  public openCheckInCartItemsDialog() {
    this.itemsInCart$.pipe(
      first(),
      switchMap((items) => {
        // Filter for only shipped items
        const shippedItems = items.filter(item => item.status === ItemStatus.Shipped);
        
        if (shippedItems.length === 0) {
          // Show a message that no shipped items are in the cart
          alert('No shipped items found in cart. Only items with "Shipped" status can be checked in.');
          return of(false);
        }

        const dialogRef = this.dialog.open(CheckInCartItemsDialogComponent, {
          minWidth: '40%',
          disableClose: true,
          data: {
            itemCount: shippedItems.length,
          },
        });

        return dialogRef.afterClosed();
      }),
      switchMap((confirmed: boolean) => {
        if (confirmed) {
          return this.checkInAllItems();
        } else {
          return of(false);
        }
      }),
    ).subscribe();
  }

  private checkInAllItems() {
    return this.itemsInCart$.pipe(
      first(),
      switchMap((items) => {
        // Only process shipped items
        const shippedItems = items.filter(item => item.status === ItemStatus.Shipped);
        const currentDate = new Date().toISOString();
        
        const updates = shippedItems.map((item) => {
          return this.itemsService
            .update({
              ...item,
              status: ItemStatus.InStock,
              lastInventoriedDate: currentDate,
            })
            .pipe(tap((updatedItem) => this.removeFromCart(updatedItem.id)));
        });

        return combineLatest(updates);
      }),
    );
  }
}
